import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  RefreshCw, Plane, Calendar, Eye, Download, LifeBuoy, XCircle,
  Search, ChevronLeft, ChevronRight, Filter, Inbox, ArrowUpDown,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import RaiseTicketModal from '../../components/RaiseTicketModal';
import RiyaCancelModal from '../../components/RiyaCancelModal';
import RiyaRescheduleModal from '../../components/RiyaRescheduleModal';
import { getApiBookingRouteId, getUserFacingBookingId } from '../../utils/userFacingBookingId';
import BookingStatusChips from '../../components/booking/BookingStatusChips';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

const MyBookings = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [downloadingId, setDownloadingId] = useState(null);
  const [supportBooking, setSupportBooking] = useState(null);
  const [riyaCancelBooking, setRiyaCancelBooking] = useState(null);
  const [riyaCancelMode, setRiyaCancelMode] = useState('cancel');
  const [riyaRescheduleBooking, setRiyaRescheduleBooking] = useState(null);

  // ── UI controls ──
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState({ key: 'created_at', dir: 'desc' });

  const handleDownloadTicket = async (bookingId) => {
    setDownloadingId(bookingId);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/bookings/download-eticket/${bookingId}`,
        { responseType: 'blob', withCredentials: true },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `eticket_${bookingId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('E-ticket downloaded');
    } catch (error) {
      console.error('Error downloading e-ticket:', error);
      toast.error('Failed to download e-ticket. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const fetchBookings = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/user/bookings`, {
        withCredentials: true,
        params: { page, limit: 10 },
      });
      setBookings(response.data.bookings || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookings([]);
      toast.error('Could not load bookings');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, page]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  // Derived list — filter + search + sort
  const filtered = useMemo(() => {
    let rows = [...bookings];
    if (statusFilter !== 'all') {
      rows = rows.filter((b) => {
        const s = (b.status || b.booking_status || '').toLowerCase();
        if (statusFilter === 'upcoming') return ['confirmed', 'pending_supplier_confirmation', 'ticketed', 'success'].includes(s);
        if (statusFilter === 'completed') return s === 'completed';
        if (statusFilter === 'cancelled') return s === 'cancelled';
        return true;
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      rows = rows.filter((b) => {
        const ref = String(getUserFacingBookingId(b) || '').toLowerCase();
        const pnr = String(b.pnr || '').toLowerCase();
        const route = String(b.route || '').toLowerCase();
        const pax = String(b.passenger_name || '').toLowerCase();
        return ref.includes(q) || pnr.includes(q) || route.includes(q) || pax.includes(q);
      });
    }
    rows.sort((a, b) => {
      const av = a[sortBy.key] ?? '';
      const bv = b[sortBy.key] ?? '';
      const cmp = (av > bv) - (av < bv);
      return sortBy.dir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [bookings, statusFilter, searchQuery, sortBy]);

  const toggleSort = (key) => {
    setSortBy((prev) => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'desc' });
  };

  return (
    <div data-testid="b2c-my-bookings-page">
      {/* Header strip */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">My Bookings</h1>
          <p className="text-sm text-gray-500">Track tickets, refunds and travel dates in one place.</p>
        </div>
        <button
          onClick={fetchBookings}
          className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors self-start sm:self-auto"
          data-testid="bookings-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Controls — filter chips + search */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 md:p-4 mb-4 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 lg:pb-0">
          <Filter className="w-4 h-4 text-gray-400 shrink-0" />
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                statusFilter === f.key
                  ? 'bg-[#1B375B] text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by PNR, booking ID, route, traveler…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B375B]/30 focus:border-[#1B375B]"
            data-testid="bookings-search"
          />
        </div>
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-gray-400" />
          <p className="text-gray-500">Loading bookings…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center" data-testid="bookings-empty-state">
          <Inbox className="w-14 h-14 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-700 font-semibold mb-1">No bookings found</p>
          <p className="text-sm text-gray-500 mb-4">
            {bookings.length === 0
              ? 'Your flight bookings will appear here.'
              : 'No bookings match the current filter/search.'}
          </p>
          {bookings.length === 0 && (
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1B375B] text-white rounded-lg font-semibold hover:bg-[#335E77] transition-colors"
            >
              <Plane className="w-4 h-4" /> Search a flight
            </button>
          )}
        </div>
      ) : (
        <>
          {/* DESKTOP — table */}
          <div className="hidden lg:block bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="bookings-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <Th onClick={() => toggleSort('booking_id')} active={sortBy.key === 'booking_id'} dir={sortBy.dir}>Booking</Th>
                    <Th>Route</Th>
                    <Th onClick={() => toggleSort('travel_date')} active={sortBy.key === 'travel_date'} dir={sortBy.dir}>Travel Date</Th>
                    <Th>Traveler</Th>
                    <Th align="right" onClick={() => toggleSort('total_amount')} active={sortBy.key === 'total_amount'} dir={sortBy.dir}>Amount</Th>
                    <Th>Status</Th>
                    <Th align="right">Actions</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((booking) => (
                    <BookingRow
                      key={booking.id}
                      booking={booking}
                      downloadingId={downloadingId}
                      onDownload={handleDownloadTicket}
                      onView={(id) => navigate(`/booking-details/${id}`)}
                      onSupport={(b) => setSupportBooking(b)}
                      onRiyaCancel={(b, mode) => { setRiyaCancelMode(mode); setRiyaCancelBooking(b); }}
                      onRiyaReschedule={(b) => setRiyaRescheduleBooking(b)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* MOBILE — stacked cards */}
          <div className="lg:hidden space-y-3">
            {filtered.map((booking) => (
              <BookingCard
                key={booking.id}
                booking={booking}
                downloadingId={downloadingId}
                onDownload={handleDownloadTicket}
                onView={(id) => navigate(`/booking-details/${id}`)}
                onSupport={(b) => setSupportBooking(b)}
                onRiyaCancel={(b, mode) => { setRiyaCancelMode(mode); setRiyaCancelBooking(b); }}
                onRiyaReschedule={(b) => setRiyaRescheduleBooking(b)}
              />
            ))}
          </div>
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="inline-flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="bookings-prev"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <span className="px-4 py-2 text-sm font-medium text-gray-700">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="inline-flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="bookings-next"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Modals */}
      <RaiseTicketModal
        open={!!supportBooking}
        onClose={() => setSupportBooking(null)}
        booking={supportBooking || {}}
        defaultEmail={user?.email || ''}
        defaultPhone={user?.phone || ''}
        onCreated={() => { setSupportBooking(null); navigate('/user/dashboard/tickets'); }}
      />
      <RiyaCancelModal
        open={!!riyaCancelBooking}
        onClose={() => setRiyaCancelBooking(null)}
        bookingId={getApiBookingRouteId(riyaCancelBooking) || riyaCancelBooking?.id || riyaCancelBooking?.booking_id}
        booking={riyaCancelBooking || {}}
        mode={riyaCancelMode}
        onCancelled={() => { setRiyaCancelBooking(null); fetchBookings(); }}
      />
      <RiyaRescheduleModal
        open={!!riyaRescheduleBooking}
        onClose={() => setRiyaRescheduleBooking(null)}
        booking={riyaRescheduleBooking || {}}
      />
    </div>
  );
};

const Th = ({ children, onClick, active, dir, align = 'left' }) => (
  <th
    className={`px-4 py-3 text-xs font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap ${
      align === 'right' ? 'text-right' : 'text-left'
    } ${onClick ? 'cursor-pointer hover:text-gray-900 select-none' : ''}`}
    onClick={onClick}
  >
    <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
      {children}
      {onClick && (
        <ArrowUpDown className={`w-3 h-3 ${active ? 'text-[#1B375B]' : 'text-gray-300'} ${active && dir === 'asc' ? 'rotate-180' : ''}`} />
      )}
    </span>
  </th>
);

const BookingRow = ({ booking, downloadingId, onDownload, onView, onSupport, onRiyaCancel, onRiyaReschedule }) => {
  const routeId = getApiBookingRouteId(booking) || booking.id || booking.booking_id;
  const displayRef = getUserFacingBookingId(booking);
  const isRiya = ((booking.source || '').toLowerCase() === 'riya') || booking.riya_pnr;
  const statusLower = (booking.status || '').toLowerCase();
  const showCancel = isRiya && statusLower !== 'cancelled';
  const showReschedule = isRiya && ['confirmed', 'ticketed', 'success'].includes(statusLower);

  const travelDate = booking.travel_date
    ? new Date(booking.travel_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  return (
    <tr className="hover:bg-gray-50/60 transition-colors" data-testid={`booking-row-${routeId}`}>
      {/* Booking ID + PNR */}
      <td className="px-4 py-3.5 whitespace-nowrap">
        <div className="font-mono text-xs text-[#1B375B] font-semibold">{displayRef}</div>
        <div className="text-[11px] text-gray-500 mt-0.5">
          PNR: <span className="font-mono font-medium text-gray-700">{booking.pnr || 'Pending'}</span>
        </div>
        <div className="text-[10px] text-gray-400 mt-0.5">
          Booked {new Date(booking.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
        </div>
      </td>

      {/* Route */}
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-1.5 font-semibold text-gray-900 text-sm">
          <span>{booking.origin || '—'}</span>
          <Plane className="w-3.5 h-3.5 text-gray-400 -rotate-45" />
          <span>{booking.destination || '—'}</span>
        </div>
        <div className="text-[11px] text-gray-500 mt-0.5 max-w-[180px] truncate">{booking.route || ''}</div>
      </td>

      {/* Travel date */}
      <td className="px-4 py-3.5 whitespace-nowrap">
        <div className="inline-flex items-center gap-1.5 text-sm text-gray-700">
          <Calendar className="w-3.5 h-3.5 text-gray-400" />
          {travelDate}
        </div>
      </td>

      {/* Traveler */}
      <td className="px-4 py-3.5">
        <div className="text-sm text-gray-800 truncate max-w-[180px]" title={booking.passenger_name}>
          {booking.passenger_name || 'Passenger'}
        </div>
        <div className="text-[11px] text-gray-500">
          {booking.total_passengers || 1} pax
        </div>
      </td>

      {/* Amount */}
      <td className="px-4 py-3.5 text-right whitespace-nowrap">
        <div className="text-sm font-bold text-gray-900">
          ₹{Number(booking.total_amount || 0).toLocaleString('en-IN')}
        </div>
        {Array.isArray(booking.ssr_breakdown) && booking.ssr_breakdown.length > 0 && (
          <div className="text-[10px] text-purple-700 mt-0.5" data-testid={`addons-summary-${routeId}`}>
            + ₹{Number(booking.ssr_amount || 0).toLocaleString('en-IN')} add-ons
          </div>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-3.5">
        <BookingStatusChips booking={booking} className="items-start" />
        {booking.cancellation_details && (
          <div className="text-[10px] text-amber-700 mt-1" data-testid={`refund-chip-${routeId}`}>
            Refund ₹{Number(booking.cancellation_details.refund_amount || 0).toLocaleString('en-IN')}
            · {String(booking.cancellation_details.refund_status || 'pending').replace('_', ' ')}
          </div>
        )}
      </td>

      {/* Actions */}
      <td className="px-4 py-3.5 text-right whitespace-nowrap">
        <div className="inline-flex items-center gap-1">
          <IconBtn
            title="View details"
            onClick={() => onView(routeId)}
            icon={Eye}
            testid={`view-btn-${routeId}`}
            color="bg-[#1B375B] hover:bg-[#335E77] text-white"
          />
          <IconBtn
            title="Download e-ticket"
            onClick={() => onDownload(routeId)}
            icon={Download}
            testid={`download-btn-${routeId}`}
            color="bg-emerald-600 hover:bg-emerald-700 text-white"
            loading={downloadingId === routeId}
          />
          <IconBtn
            title="Need help"
            onClick={() => onSupport(booking)}
            icon={LifeBuoy}
            testid={`help-btn-${routeId}`}
            color="bg-orange-500 hover:bg-orange-600 text-white"
          />
          {showReschedule && (
            <IconBtn
              title="Reschedule"
              onClick={() => onRiyaReschedule(booking)}
              icon={Calendar}
              testid={`reschedule-btn-${routeId}`}
              color="bg-indigo-600 hover:bg-indigo-700 text-white"
            />
          )}
          {showCancel && (
            <IconBtn
              title="Cancel booking"
              onClick={() => {
                const s = (booking.riya_status || booking.status || '').toLowerCase();
                const isHeld = ['on_hold', 'hold', 'pending'].includes(s);
                onRiyaCancel(booking, isHeld ? 'hold-cancel' : 'cancel');
              }}
              icon={XCircle}
              testid={`cancel-btn-${routeId}`}
              color="bg-red-600 hover:bg-red-700 text-white"
            />
          )}
        </div>
      </td>
    </tr>
  );
};

const IconBtn = ({ icon: Icon, onClick, title, testid, color, loading }) => (
  <button
    onClick={onClick}
    disabled={loading}
    title={title}
    aria-label={title}
    data-testid={testid}
    className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed ${color}`}
  >
    {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
  </button>
);

const BookingCard = ({ booking, downloadingId, onDownload, onView, onSupport, onRiyaCancel, onRiyaReschedule }) => {
  const routeId = getApiBookingRouteId(booking) || booking.id || booking.booking_id;
  const displayRef = getUserFacingBookingId(booking);
  const isRiya = ((booking.source || '').toLowerCase() === 'riya') || booking.riya_pnr;
  const statusLower = (booking.status || '').toLowerCase();
  const showCancel = isRiya && statusLower !== 'cancelled';
  const showReschedule = isRiya && ['confirmed', 'ticketed', 'success'].includes(statusLower);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid={`booking-card-${routeId}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-[#1B375B] font-semibold">{displayRef}</div>
          <div className="font-semibold text-gray-900 mt-1 truncate">{booking.route || `${booking.origin || ''} → ${booking.destination || ''}`}</div>
          <div className="text-xs text-gray-500 mt-0.5">PNR: <span className="font-mono">{booking.pnr || 'Pending'}</span></div>
        </div>
        <BookingStatusChips booking={booking} className="items-end shrink-0" />
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        <div>
          <div className="text-gray-500">Travel</div>
          <div className="font-medium text-gray-800">
            {booking.travel_date ? new Date(booking.travel_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
          </div>
        </div>
        <div>
          <div className="text-gray-500">Passengers</div>
          <div className="font-medium text-gray-800">{booking.total_passengers || 1}</div>
        </div>
        <div className="col-span-2">
          <div className="text-gray-500">Amount</div>
          <div className="font-bold text-gray-900">₹{Number(booking.total_amount || 0).toLocaleString('en-IN')}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-3 border-t border-gray-100 overflow-x-auto">
        <IconBtn title="View" onClick={() => onView(routeId)} icon={Eye} testid={`view-btn-${routeId}`} color="bg-[#1B375B] text-white" />
        <IconBtn title="Download" onClick={() => onDownload(routeId)} icon={Download} testid={`download-btn-${routeId}`} color="bg-emerald-600 text-white" loading={downloadingId === routeId} />
        <IconBtn title="Help" onClick={() => onSupport(booking)} icon={LifeBuoy} testid={`help-btn-${routeId}`} color="bg-orange-500 text-white" />
        {showReschedule && <IconBtn title="Reschedule" onClick={() => onRiyaReschedule(booking)} icon={Calendar} testid={`reschedule-btn-${routeId}`} color="bg-indigo-600 text-white" />}
        {showCancel && (
          <IconBtn
            title="Cancel"
            onClick={() => {
              const s = (booking.riya_status || booking.status || '').toLowerCase();
              const isHeld = ['on_hold', 'hold', 'pending'].includes(s);
              onRiyaCancel(booking, isHeld ? 'hold-cancel' : 'cancel');
            }}
            icon={XCircle}
            testid={`cancel-btn-${routeId}`}
            color="bg-red-600 text-white"
          />
        )}
      </div>
    </div>
  );
};

export default MyBookings;
